import { eTlfItemType } from "functions/api/enums";
import { applyVariantPatches } from "functions/tlf/applyVariantPatches";
import { pickVariantUpdateable } from "functions/tlf/pickVariantUpdateable";
import { Patch } from "rfc6902";
import { getVariantPatchContext } from "./getVariantPatchContext";
import { SET_VARIANT_PATCH_SELECTION, TlfContextProps, UPDATE_TLF } from "./interface";
import { tlf_setVariantPatchSelection } from "./tlf_setVariantPatchSelection";
import { tlf_updateTlf } from "./tlf_updateTlf";

/**
 * Applies a patch selection and records both its composition and selection state.
 *
 * @param state - Current TLF context state
 * @param patchNames - Patch names to apply
 * @param catalog - Patch catalog after committing the active patch
 * @param activeName - Patch that should receive edits after selection
 * @param calledFrom - Reducer action origin
 * @returns State with the composed item and selection
 */
export const applySelection = (
  state: TlfContextProps,
  patchNames: string[],
  catalog: Record<string, Patch>,
  activeName: string,
  calledFrom: string,
): TlfContextProps => {
  const { master, information } = getVariantPatchContext(state);
  if (master === null || information === null) return state;
  const composed = applyVariantPatches(
    master,
    { ...information, allVariantPatches: catalog },
    patchNames,
  );
  // Overlapping patches are rejected before selection reaches this helper.
  // istanbul ignore next
  if (!composed.success) return state;
  const tlfData =
    composed.data.type === eTlfItemType.arsListing
      ? {
          type: composed.data.type,
          ...pickVariantUpdateable(composed.data),
          allVariantPatches: catalog,
        }
      : {
          type: composed.data.type,
          ...pickVariantUpdateable(composed.data),
          allVariantPatches: catalog,
        };
  const updated = tlf_updateTlf(state, {
    operation: UPDATE_TLF,
    tlfData,
    calledFrom,
  });
  const selectedName = patchNames.includes(activeName) ? activeName : (patchNames[0] ?? null);
  if (selectedName === null) {
    return tlf_setVariantPatchSelection(updated, {
      operation: SET_VARIANT_PATCH_SELECTION,
      appliedPatchNames: [],
      activePatchName: activeName,
      calledFrom,
    });
  }
  return tlf_setVariantPatchSelection(updated, {
    operation: SET_VARIANT_PATCH_SELECTION,
    appliedPatchNames: patchNames,
    activePatchName: selectedName,
    calledFrom,
  });
};
